import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { Crumb, ElementSummary, WhatsNew, WhatsNewEntry, WhatsNewGroup } from "../../../api/types";
import { Breadcrumb, Button, EmptyState, Pill, StateBadge } from "../../../ui";
import { displayState } from "../../../ui/state";
import { useNow } from "../hooks";
import { NAVIGATOR_KINDS } from "../model/replay";
import { formatWhen } from "../model/time";
import { CHANGE_WORDS, isReturned, orderWhatsNew, shortList, SHORT_LIST } from "../model/whatsNew";
import { kindWord, navigatorHref } from "../model/words";

/** Where an entry leads: a tree node opens itself; anything else opens the item it sits under. */
export function entryTarget(group: WhatsNewGroup, entry: WhatsNewEntry): string {
  if (NAVIGATOR_KINDS.has(entry.element.kind) || !group.item) return navigatorHref(entry.element.id);
  return navigatorHref(group.item.element_id);
}

function crumbLink(crumb: Crumb): string | null {
  return crumb.element_id ? navigatorHref(crumb.element_id) : null;
}

function look(element: ElementSummary) {
  return displayState(element.state as Parameters<typeof displayState>[0], { origin: element.origin });
}

function EntryRow({ group, entry, now }: { group: WhatsNewGroup; entry: WhatsNewEntry; now: Date }) {
  const state = look(entry.element);
  return (
    <li className="nv-news__entry" data-change={entry.change}>
      <span className="nv-news__change" data-returned={isReturned(entry.change) || undefined}>
        {CHANGE_WORDS[entry.change]}
      </span>{" "}
      <Link to={entryTarget(group, entry)} className="nv-news__name">
        {entry.element.name}
      </Link>{" "}
      <span className="muted">({kindWord(entry.element.kind)})</span>
      {state && entry.change !== "removed" && (
        <>
          {" "}
          <StateBadge state={state} />
        </>
      )}
      <span className="nv-news__who muted">
        {" "}
        by {entry.by.display}, {formatWhen(entry.at, now)}
      </span>
      {entry.your_own_work && (
        <>
          {" "}
          <Pill>Your own work</Pill>
        </>
      )}
    </li>
  );
}

function Group({ group, now }: { group: WhatsNewGroup; now: Date }) {
  const title = group.item?.name ?? "Not part of one item";
  return (
    <section className="nv-news__group" aria-label={title}>
      {group.breadcrumb.length > 0 ? <Breadcrumb crumbs={group.breadcrumb} linkFor={crumbLink} /> : <h3 className="nv-news__title">{title}</h3>}
      <ul className="nv-news__entries">
        {group.entries.map((entry) => (
          <EntryRow key={`${entry.element.id}:${entry.change}:${entry.at}`} group={group} entry={entry} now={now} />
        ))}
      </ul>
    </section>
  );
}

interface WhatsNewListProps {
  news: WhatsNew;
  /** "panel": about eight, then a link to the page. "page": about eight, then a button for the rest. */
  mode: "panel" | "page";
  /** What the news is compared with, for the sentence when nothing is new: "since your last visit". */
  since?: string;
  /** On the page: asks the server for the changes it left out. Without it the page says only how many are left out. */
  onShowEverything?: () => void;
  /** The rest is being read. */
  loadingEverything?: boolean;
}

/**
 * The net change for the reader (workflow 22), grouped by item with the family breadcrumb, returned work first,
 * about eight entries with the rest on request, each linked to the family navigator.
 */
export function WhatsNewList({ news, mode, since = "since your last visit", onShowEverything, loadingEverything = false }: WhatsNewListProps) {
  const now = useNow();
  const [showAll, setShowAll] = useState(false);
  const ordered = useMemo(() => orderWhatsNew(news), [news]);
  const list = useMemo(() => shortList(ordered, mode === "page" && showAll ? Infinity : SHORT_LIST), [ordered, mode, showAll]);

  if (news.first_visit) {
    return (
      <EmptyState title="This is your first visit" action={<Link to="/navigate">Open the family navigator</Link>}>
        There is no earlier visit to compare with, so nothing is new yet. The family tree shows everything there is.
      </EmptyState>
    );
  }

  const nothing = list.groups.length === 0 && news.more <= 0;
  return (
    <div className="nv-news">
      {nothing && news.your_waiting.length === 0 ? (
        <EmptyState title="Nothing is new" action={<Link to="/navigate">Open the family navigator</Link>}>
          Nothing has changed {since}.
        </EmptyState>
      ) : (
        <>
          {list.groups.map((group) => (
            <Group key={`${group.item?.element_id ?? "none"}:${group.entries[0]?.element.id ?? ""}`} group={group} now={now} />
          ))}
          {list.hidden > 0 && mode === "panel" && (
            <p>
              <Link to="/whats-new">See {list.hidden === 1 ? "the other change" : `the other ${list.hidden} changes`}</Link>
            </p>
          )}
          {list.hidden > 0 && mode === "page" && list.held > 0 && (
            <p>
              <Button variant="secondary" onClick={() => setShowAll(true)}>
                {list.held === 1 ? "Show the other change" : `Show the other ${list.held} changes`}
              </Button>
            </p>
          )}
          {mode === "page" && (showAll || list.held === 0) && news.more > 0 && onShowEverything && (
            <p>
              <Button variant="secondary" busy={loadingEverything} onClick={onShowEverything}>
                {news.more === 1 ? "Show the 1 change that is left out" : `Show the ${news.more} changes that are left out`}
              </Button>{" "}
              <span className="muted">
                The <Link to="/live">live view</Link> replays everything.
              </span>
            </p>
          )}
          {mode === "page" && (showAll || list.held === 0) && news.more > 0 && !onShowEverything && (
            <p className="muted">
              {news.more === 1 ? "1 more change is" : `${news.more} more changes are`} not listed here. The <Link to="/live">live view</Link> replays everything.
            </p>
          )}
        </>
      )}
      {news.your_waiting.length > 0 && (
        <section className="nv-news__waiting" aria-label="Your work waiting for the SME">
          <h3>Your work waiting for the SME ({news.your_waiting.length})</h3>
          <ul>
            {news.your_waiting.map((element) => (
              <li key={element.id}>
                <Link to={navigatorHref(element.id)}>{element.name}</Link> <span className="muted">({kindWord(element.kind)})</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
